// EncSectionCard · the one card of a frame v2 encounter step (design export 21-10, components/EncSectionCard; ENCOUNTER-SPEC
// rows 5, 6, 12). A white card (card radius, hairline, no shadow, the card padding inside) that holds the step's blocks. Each
// block is a 32 tile tinted in the step's SOAP colour (Subjective plum, Objective grey, Assessment green, Plan blue) with its
// glyph, a card-title heading, and the block's actions at the right; blocks are joined by a 2px spine in the same colour.
// Used by the pre-visit, the questionnaire steps, A/P, Wrap-up and the note, so they all read as one family.
//
// Local stand-in: Signal DS has no card with a tiled, spined block list (PROD-3310). The design's CSS module becomes data hooks
// here (the PA ships no CSS modules); the look lives in src/styles/showcase/enc-section-card.css, keyed on:
//   [data-slot="enc-section-card"][data-soap]  the card            [data-enc-block][data-soap]  one block
//   [data-slot="enc-block-tile"][data-icon]    the 32 tile         [data-slot="enc-block-glyph"]  the masked glyph
//   [data-slot="enc-block-title"]              the heading         [data-slot="enc-block-actions"] right of the title
//   [data-slot="enc-block-sub"]                a line under it     [data-slot="enc-block-body"]    the block's content
// The SOAP colours (`--enc-tint`, `--enc-line`, `--enc-glyph`) come from the encounter one-look sheet, keyed on `data-soap`.
import { createContext, useContext, type ReactNode } from 'react';

export type EncSoap = 's' | 'o' | 'a' | 'p';
export type EncIcon = 'hpi' | 'risk' | 'ros' | 'pe' | 'vitals' | 'docs' | 'assessment' | 'plan' | 'pill' | 'done';

/** The glyph a SOAP section's block wears when the step names none. */
export const ENC_SOAP_ICON: Record<EncSoap, EncIcon> = { s: 'hpi', o: 'vitals', a: 'assessment', p: 'plan' };

/**
 * Spec row 6: a template step maps to its nearest SOAP section: intake and history to S, measures and tests to O, assessment and
 * diagnosis to A, plan, prescription, goals and wrap-up to P.
 */
export function encSoapForLabel(label: string, fallback: EncSoap = 's'): EncSoap {
  const t = label.toLowerCase();
  if (/a\/p|assessment (and|&) plan/.test(t)) return 'a';
  if (/wrap|plan|prescri|goal|handover|intervention|agreed|monitor|steps|\bsigns?\b/.test(t)) return 'p';
  if (/diagnos|assess|clearance|readiness|review|decision/.test(t)) return 'a';
  if (/objective|measure|test|result|vital|exam|\bpe\b|doc|response|pre-visit|pre visit|scale|score/.test(t)) return 'o';
  if (/subjective|intake|history|interval|screen|check-in|lifestyle|hpi|risk|ros|data|behaviour|complaint|meeting|session|coaching/.test(t)) return 's';
  return fallback;
}

const Ctx = createContext<EncSoap | null>(null);
/** The SOAP section of the card around, or null outside one. */
export function useEncSection(): EncSoap | null {
  return useContext(Ctx);
}
const BlockCtx = createContext(false);
/** True inside a block's body (a section there is plain content, not another block). */
export function useEncInBlock(): boolean {
  return useContext(BlockCtx);
}

export interface EncBlockProps {
  title: ReactNode;
  icon?: EncIcon;
  /** The block's actions, right of the title (tertiary buttons). */
  actions?: ReactNode;
  id?: string;
  /** Under the title, in the block's column. */
  children?: ReactNode;
  /** A line under the title row (the template's name, a hint). */
  sub?: ReactNode;
  /** One block of a card in another SOAP colour (A/P: Diagnoses green, Orders blue). */
  section?: EncSoap;
  /** An accessible name for the block's region when the title is not plain text. */
  'aria-label'?: string;
}

export function EncBlock({ title, icon, actions, id, children, sub, section: own, 'aria-label': ariaLabel }: EncBlockProps) {
  const ctx = useEncSection();
  const section = own ?? ctx ?? 's';
  return (
    <section data-enc-block="" data-soap={section} id={id} aria-label={ariaLabel}>
      <header data-slot="enc-block-head">
        <span data-slot="enc-block-tile" data-icon={icon ?? ENC_SOAP_ICON[section]} aria-hidden>
          <span data-slot="enc-block-glyph" />
        </span>
        <h3 data-slot="enc-block-title">{title}</h3>
        {actions ? <span data-slot="enc-block-actions">{actions}</span> : null}
      </header>
      {sub != null ? <div data-slot="enc-block-sub">{sub}</div> : null}
      {children != null ? (
        <BlockCtx.Provider value>
          <div data-slot="enc-block-body">{children}</div>
        </BlockCtx.Provider>
      ) : null}
    </section>
  );
}

export interface EncSectionCardProps {
  section: EncSoap;
  blocks?: (EncBlockProps & { key?: string })[];
  children?: ReactNode;
  id?: string;
  'aria-label'?: string;
}

export function EncSectionCard({ section, blocks, children, id, 'aria-label': ariaLabel }: EncSectionCardProps) {
  return (
    <Ctx.Provider value={section}>
      <section id={id} aria-label={ariaLabel} data-slot="enc-section-card" data-soap={section}>
        {blocks ? blocks.map(({ key, ...b }, i) => <EncBlock key={key ?? b.id ?? i} {...b} />) : null}
        {children}
      </section>
    </Ctx.Provider>
  );
}
